import { useState, useRef, useEffect, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import styled from 'styled-components'
import _getOr from 'lodash/fp/getOr'

import Button from '../Button'
import { getCurrentApp } from '../../apps/selectors'
import { verifyEkyc, openNewRemoteSellingPreviewInfoModal } from '../../apps/actions'
import { FormFeedback } from 'reactstrap'
import { getAppConfig } from '../../../deploy-env/app-config'
import InsuredAndPayerDetailsConfirmation from 'e-submission/containers/Dialog/InsuredAndPayerDetailsConfirmation'
import { insurerDoesEkyc, payerDoesEkyc } from 'e-submission/domain/data-model/form/selectors'
import { IsRealDevice } from 'e-submission/utils'

const RefreshButton = styled(Button)`
  margin-top: 0.5rem;
  float: right;
  font-weight: bold;
`

function findParentNodeTarget(domElement) {
  if (!domElement) return null
  if (domElement && domElement.className.match(/FormEngine/)) {
    return domElement
  } else {
    const parentDom = domElement.parentNode
    return findParentNodeTarget(parentDom)
  }
}

const EkycDocumentFooter = ({
  insuredDopaRequestFailed,
  payerDopaRequestFailed,
  verify,
  showRefreshButton,
  isSelectedRemoteSelling,
  openNewRemoteSellingPreviewInfoModal,
}) => {
  const [isDisable, setIsDisable] = useState(false)
  const refRootComponent = useRef(null)

  useEffect(() => {
    const formEngineDom = findParentNodeTarget(refRootComponent.current)

    if (formEngineDom && !IsRealDevice) {
      formEngineDom.style.maxWidth = '900px'
    }

    return () => {
      if (isSelectedRemoteSelling) {
        openNewRemoteSellingPreviewInfoModal()
      }

      if (formEngineDom && !IsRealDevice) {
        formEngineDom.style.maxWidth = ''
      }
    }
  }, [isSelectedRemoteSelling, openNewRemoteSellingPreviewInfoModal])

  const handleRefreshClick = useCallback(() => {
    setIsDisable(true)
    verify('insured')
    verify('payer')
    setTimeout(() => setIsDisable(false), getAppConfig().VERIFY_EKYC_INTERVAL_TIME)
  }, [verify])

  return (
    <div ref={refRootComponent}>
      {insuredDopaRequestFailed || payerDopaRequestFailed ? (
        <FormFeedback className="info-message attached-icon">
          <p>ขณะนี้ระบบ DOPA ขัดข้อง ท่านสามารถกดยืนยัน eKYC เพื่อดำเนินการยืนยันตัวตนในขั้นตอนต่อไป</p>
        </FormFeedback>
      ) : null}
      {showRefreshButton ? (
        <div style={{ textAlign: 'right' }}>
          <RefreshButton disabled={isDisable} onClick={handleRefreshClick}>
            อัปเดตสถานะ
          </RefreshButton>
        </div>
      ) : null}
      <InsuredAndPayerDetailsConfirmation />
    </div>
  )
}

const EkycDocumentFooterContainer = () => {
  const dispatch = useDispatch()
  const app = useSelector((state) => getCurrentApp(state))

  const insuredDopaRequestFailed = _getOr(
    false,
    'ekycInfo.insured.verificationDetail.dopaResult.dopaRequestFailed',
    app
  )
  const payerDopaRequestFailed = _getOr(false, 'ekycInfo.payer.verificationDetail.dopaResult.dopaRequestFailed', app)
  const showRefreshButton =
    _getOr(false, 'ekycInfo.payer.proprietorId', app) || _getOr(false, 'ekycInfo.insured.proprietorId', app)
  const isSelectedRemoteSelling = false

  const verify = useCallback((insuredOrPayer) => dispatch(verifyEkyc(insuredOrPayer)), [dispatch])

  const openNewRemoteSellingPreviewInfoModalHandler = useCallback(
    () => dispatch(openNewRemoteSellingPreviewInfoModal()),
    [dispatch]
  )

  return (
    <EkycDocumentFooter
      insuredDopaRequestFailed={insuredDopaRequestFailed}
      payerDopaRequestFailed={payerDopaRequestFailed}
      verify={verify}
      showRefreshButton={showRefreshButton}
      isSelectedRemoteSelling={isSelectedRemoteSelling}
      openNewRemoteSellingPreviewInfoModal={openNewRemoteSellingPreviewInfoModalHandler}
    />
  )
}

export default EkycDocumentFooterContainer
